import { useTranslation } from "react-i18next";
import { Checkbox } from "react-aria-components";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * "Bereiche editieren" — the drill-down's edit-mode switch (redesign 2026-10-21). Off ⇒ pure navigation: no kebabs, no
 * "add" buttons. On ⇒ the management affordances appear. Rendered only for a caller who can manage at least one facility
 * (the host decides); it never grants anything — every action stays gated by `useFacilityAuthz` and the server.
 *
 * A real react-aria `Checkbox` (keyboard + screen reader), skinned as a soft pill the same height as the filter chips.
 * The box is a rounded square, not a circle — a circle reads as a radio button (pick one of many). Its border uses
 * `muted-foreground` so the unchecked box clears 3:1 against the pill (SC 1.4.11); the design's lighter hairline would not.
 */
export function EditModeToggle({ isSelected, onChange }: { isSelected: boolean; onChange: (on: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Checkbox
      isSelected={isSelected}
      onChange={onChange}
      className="group bg-input/60 dark:bg-input text-foreground inline-flex h-9 cursor-pointer items-center gap-2.5 rounded-[10px] pr-3.5 pl-3 text-[13px] font-semibold outline-none select-none"
    >
      {({ isSelected: on, isFocusVisible }) => (
        <>
          <span
            aria-hidden="true"
            className={cn(
              "inline-flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border transition-colors",
              on ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground bg-background",
              isFocusVisible && "ring-ring/50 ring-3",
            )}
          >
            {on ? <Check className="size-3" strokeWidth={3.5} /> : null}
          </span>
          {t("facilitySelection.editMode")}
        </>
      )}
    </Checkbox>
  );
}
